import { Button } from "../../../components/buttons/button";
import { SearchInput } from "../../../components/inputs/searchInput";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { SettingsLayout } from "../../../components/layout/settingsLayout";
import { AddHolidayFlow } from "../../../components/modal/app_modals/addHolidayFlow";
import Dropdown from "../../../components/dropdown/dropdown";
import { useState } from "react";
import { currentYear, nextYear } from "../../../utils/constants";
import { TableComponent } from "../../../components/table";
import { Pill } from "../../../components/pill";
import { Holiday, QueryWithStatus } from "../../../utils/types";
import { useGetAllHolidaysQuery } from "../../../redux/features/admin/admin.api";
import { HolidayDateBadge } from "../../../components/dateBadge";
import { formatDate } from "../../../utils/helpers";
import { ConfirmPublishedHoliday } from "../../../components/modal/app_modals/confirmPublishedHoliday";
import { PayoutDelayEmailModal } from "../../../components/modal/app_modals/payoutDelayEmailModal";

const getPayoutDelayEmailStatusLabel = (
  status?: Holiday["payoutDelayEmailStatus"],
) => {
  switch (status) {
    case "sending":
      return "SENDING";
    case "sent":
      return "SENT";
    case "failed":
      return "FAILED";
    case "not-sent":
    default:
      return "NOT SENT";
  }
};

const getPayoutDelayEmailStatusVariation = (
  status?: Holiday["payoutDelayEmailStatus"],
) => {
  switch (status) {
    case "sending":
      return "yellow";
    case "sent":
      return "green";
    case "failed":
      return "red";
    case "not-sent":
    default:
      return "grey";
  }
};

const getPayoutDelayDisabledReason = (holiday: Holiday) => {
  if (holiday.status === "requires_attention") {
    return "Confirm this holiday before sending payout delay emails.";
  }

  if (!holiday.payoutDelayEmailActionId) {
    return "Payout delay email action is not available for this holiday.";
  }

  if (holiday.payoutDelayEmailStatus === "sending") {
    return (
      holiday.payoutDelayEmailEligibilityReason ??
      "Payout delay email is already sending."
    );
  }

  if (holiday.payoutDelayEmailStatus === "sent") {
    return (
      holiday.payoutDelayEmailEligibilityReason ??
      "Payout delay email has already been sent."
    );
  }

  return holiday.payoutDelayEmailEligibilityReason ?? undefined;
};

const canSendPayoutDelayEmail = (holiday: Holiday) =>
  Boolean(
    holiday.payoutDelayEmailActionId &&
      holiday.status !== "requires_attention" &&
      holiday.canSendPayoutDelayEmail &&
      holiday.payoutDelayEmailStatus !== "sending" &&
      holiday.payoutDelayEmailStatus !== "sent",
  );

export const HolidaysPage = () => {
  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();
  const [query, setQuery] = useState<
    QueryWithStatus & { year: number | undefined }
  >({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
    status: undefined,
    year: undefined,
  });

  const [year, setYear] = useState<number>(currentYear);
  const [selectHoliday, setSelectHoliday] = useState<{
    _id: string;
    name: string;
    holidayDate: string;
    nextWorkingDay: string;
  } | null>(null);
  const [selectedPayoutDelayHoliday, setSelectedPayoutDelayHoliday] =
    useState<Holiday | null>(null);

  const dropDownOptions = [
    {
      label: currentYear,
      value: currentYear,
      onClick: () => {
        setYear(currentYear);
      },
    },
    {
      label: nextYear,
      value: nextYear,
      onClick: () => {
        setYear(nextYear);
      },
    },
  ];
  const {
    isFetching: fetchingHolidays,
    data: holidayData,
    refetch: refetchHolidays,
  } = useGetAllHolidaysQuery(
    { ...query, year, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  return (
    <div className="w-full md:flex md:h-full md:flex-col">
      <PageHeader header={"Settings"} className="font-fraunces font-normal" />
      <main className="mt-10 flex flex-col items-start gap-[30px] md:min-h-0 md:flex-1 md:flex-row md:gap-[80px]">
        <SettingsLayout />
        <div className="flex w-full flex-col gap-[2px] md:min-h-0 md:flex-1 md:self-stretch">
          <div className="flex h-[58px] cursor-pointer items-center justify-between rounded-2xl bg-white p-3">
            <h1 className="font-fustat text-base font-bold text-grey-950">
              Public Holidays
            </h1>
            <Button
              text="Add Holiday"
              icon="plus"
              type="button"
              onClick={() => {
                handleModalOpen("addHolidayModal");
              }}
              variation="blue"
              iconClassName={"mb-1"}
              className="max-w-fit"
            />
          </div>
          <BgWrapper className="flex flex-col gap-3 rounded-t-2xl md:min-h-0 md:flex-1">
            <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
              <SearchInput
                searchFunction={(value) => {
                  setQuery({
                    ...query,
                    search: value,
                    page: 1,
                  });
                }}
                placeholder="Search"
                className="md:!max-w-[300px]"
              />
              <Dropdown
                menuItems={dropDownOptions.map((option) => {
                  return {
                    name: (
                      <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                        {option.label}
                      </p>
                    ),
                    onClick: option.onClick,
                  };
                })}
                className=""
                menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px]"
                menuItemsClassName="border-none !text-base !bg-transparent !p-0"
                position="bottom end"
              >
                <Button
                  text={year}
                  icon="caret-down"
                  type="button"
                  iconFirst={false}
                  variation="styled-grey"
                  iconClassName={"mb-1"}
                  className="max-w-fit"
                />
              </Dropdown>
            </div>
            <TableComponent
              headers={[]}
              rows={
                holidayData?.data?.holidays?.map((holiday, idx) => {
                  return {
                    id: idx,
                    items: [
                      <div
                        onClick={() => {
                          if (holiday?.status === "requires_attention") {
                            setSelectHoliday({
                              _id:
                                holiday._id ??
                                holiday.payoutDelayEmailActionId ??
                                holiday.holidayKey ??
                                "",
                              name: holiday.name,
                              nextWorkingDay: holiday.nextWorkingDay,
                              holidayDate: holiday.holidayDate,
                            });
                            handleModalOpen("confirmPublishedHolidayModal");
                          }
                        }}
                        className={`flex w-full flex-1 flex-col justify-between gap-4 md:flex-row md:items-center`}
                      >
                        <div className="flex min-w-0 items-center gap-[2px]">
                          <div className="mr-2">
                            <HolidayDateBadge
                              holidayDate={holiday?.holidayDate}
                            />
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1">
                              <p className="text-sm font-bold text-grey-900">
                                {holiday.name} ∙
                              </p>
                              <p className="text-sm text-grey-600">
                                work resumes{" "}
                                {formatDate(holiday.nextWorkingDay)}
                              </p>
                            </div>
                            {getPayoutDelayDisabledReason(holiday) && (
                              <p className="mt-1 text-xs text-grey-500">
                                {getPayoutDelayDisabledReason(holiday)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <Pill
                            text={`${holiday?.status === "requires_attention" ? "REQUIRES ATTENTION" : holiday?.status === "completed" ? "COMPLETED" : "CONFIRMED"}`}
                            variation={`${holiday?.status === "requires_attention" ? "yellow" : holiday?.status === "completed" ? "green" : "blue"}`}
                          />
                          <Pill
                            text={getPayoutDelayEmailStatusLabel(
                              holiday.payoutDelayEmailStatus,
                            )}
                            variation={getPayoutDelayEmailStatusVariation(
                              holiday.payoutDelayEmailStatus,
                            )}
                          />
                          <Button
                            text="Send payout delay email"
                            type="button"
                            size="small"
                            variation="plain-grey"
                            className="max-w-fit"
                            title={
                              !canSendPayoutDelayEmail(holiday)
                                ? getPayoutDelayDisabledReason(holiday)
                                : undefined
                            }
                            disabled={!canSendPayoutDelayEmail(holiday)}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedPayoutDelayHoliday(holiday);
                              handleModalOpen("payoutDelayEmailModal");
                            }}
                          />
                        </div>
                      </div>,
                    ],
                  };
                }) ?? []
              }
              loadingRows={query.limit}
              isLoading={fetchingHolidays}
              isRowClickable={false}
              isTableEmpty={[0, undefined].includes(
                holidayData?.data?.holidays?.length,
              )}
              emptyTitle="No Holidays"
              emptySubTitle=""
              emptyImage="/assets/images/guest-glass.png"
              isPaginated
              currentPage={query.page ?? 1}
              totalCount={holidayData?.data?.totalCount ?? 0}
              itemsPerPage={query.limit!}
              pageRangeDisplayed={3}
              onPageChange={(page) => {
                setQuery({
                  ...query,
                  page: page,
                });
              }}
              className=""
            />
          </BgWrapper>
        </div>
      </main>
      {modalOpen && modal === "addHolidayModal" && (
        <AddHolidayFlow
          open={modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          refetchHolidays={refetchHolidays}
          onSuccess={() => {
            console.log("Holiday added successfully");
          }}
        />
      )}
      {modalOpen && modal === "confirmPublishedHolidayModal" && (
        <ConfirmPublishedHoliday
          open={modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          holidayId={selectHoliday?._id ?? ""}
          holidayName={selectHoliday?.name ?? ""}
          holidayDate={selectHoliday?.holidayDate ?? ""}
          nextWorkingDay={selectHoliday?.nextWorkingDay ?? ""}
          refetchHolidays={refetchHolidays}
          onSuccess={() => {
            console.log("Holiday added successfully");
          }}
        />
      )}
      {modalOpen && modal === "payoutDelayEmailModal" && (
        <PayoutDelayEmailModal
          open={modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          actionId={
            selectedPayoutDelayHoliday?.payoutDelayEmailActionId ?? ""
          }
          onRefresh={refetchHolidays}
        />
      )}
    </div>
  );
};
